import React, { useState, useEffect, ReactNode } from "react";
import PortalPage from "../PortalPage";
import { ReactComponent as ReportingIcon } from 'assets/icons/reports-filled.svg';
import { IConfirmProps } from "../../ui/ConfirmationBox";
import { ReactComponent as BuildCircle } from "assets/icons/build-circle.svg";
import { ReactComponent as AddChart } from "assets/icons/add-chart.svg";
import { ReactComponent as DashboardCustomise } from "assets/icons/dashboard-customize.svg";
import { Checkbox, LabelledCheckbox } from '../../ui/LabelledCheckbox';
import {
    FolderOutlined, CreateNewFolderOutlined, BarChartOutlined
} from '@mui/icons-material';
import { Paper, Tabs, Tab } from "@mui/material";
import '../../ui/ConfirmationBox.css';
import { TabPanel } from '../../ui/TabPanel';
import DateRangePicker from './DateRangePicker';
import * as api from 'shared/services/reportingApi';
import { IListItemProps, List, ListItem } from "../../ui/Lists";

import { ISelectedExportColumn, standardGridSizes, ExportColumnList, ExportGrid } from './ExportGrid'

const dateToStringFormatter = (value: string) =>
    <div>{!value ? "" : (new Date(value)).toLocaleString()}</div>;

const booleanToYesNoFormatter = (value: boolean, trueValue = "Yes", falseValue = "No") =>
    <div>{!value ? falseValue : trueValue}</div>;

export const DocumentListItem = (props: IListItemProps<api.Document<api.DocumentMetadata>>) =>
    <div className="list-item" onClick={() => props.toggle(props.item)}>
        <Checkbox checkedValue={props.selected === true} />
        <span className="document-type">{props.item.type}</span>
        {props.item.subType && <span className="document-sub-type">{props.item.subType}</span>}
        {props.item.metadata.title}
    </div>;

export const DocumentTypeListItem = (props: IListItemProps<api.DocumentType>) => {
    const isSelected = (doc: api.DocumentType): boolean => {
        if (!props) {
            return false;
        }

        if (!props.selectedItems) {
            return false;
        }

        return props.selectedItems.findIndex(d => doc.id === d.id) > -1;
    };

    return (
        <>
            {(props.previousItem == null || props.previousItem.module !== props.item.module) &&
                <div className="document-type-module">{props.item.module}</div>
            }

            <ListItem {...props}>
                <LabelledCheckbox checkedValue={props.selected === true}>
                    {props.item.name}
                </LabelledCheckbox>
            </ListItem >

            {props.item.subTypes?.map(st =>
                <div key={st.id} style={{ paddingLeft: ((props.indent ?? 1) * 30) + "px" }}>
                    <DocumentTypeListItem
                        selectedItems={props.selectedItems}
                        item={st}
                        toggle={props.toggle}
                        selected={isSelected(st)}
                        previousItem={props.item}
                        indent={(props.indent ?? 1) + 1}
                    >
                    </DocumentTypeListItem>
                </div>
            )}
        </>
    );
};

const D2DocumentReport = () => {
    const MaxResultsPerReport = 60000;
    const [exportState, setExportState] = useState("ready" as "ready" | "busy");
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [exportDocuments, setExportDocuments] = useState(false);
    const [selectAllDocuments, setSelectAllDocuments] = useState(false);
    const [documentFilterTab, setDocumentFilterTab] = useState(0);
    const [documentsByNameFilter, setDocumentsByNameFilter] = useState("");
    const [documentsByNameExpanded, setDocumentsByNameExpanded] = useState(false);
    const [allDocuments, setAllDocuments] = useState([] as api.Document<api.DocumentMetadata>[]);

    const [documentTypes, setDocumentTypes] = useState([] as api.DocumentType[]);
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([] as api.DocumentType[]);
    const [documentsByTypeFilter, setDocumentsTypeFilter] = useState("");
    const [documentsByTypeExpanded, setDocumentsByTypeExpanded] = useState(false);

    const [stateVersion, setStateVersion] = useState(0);
    const [selectedDocuments, setSelectedDocuments] = useState([] as api.Document<api.DocumentMetadata>[]);

    const [exportResults, setExportResults] = useState(null as api.UsersDocumentsResult | null);

    const [publishedDateFrom, setPublishedDateFrom] = useState(null as Date | null);
    const [publishedDateTo, setPublishedDateTo] = useState(null as Date | null);

    const setPublishedDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setPublishedDateFrom(dateFrom);
        setPublishedDateTo(dateTo);
    };

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    const msgBox = (msg: ReactNode) => {
        setConfirmProps({
            message: msg,
            options: ["OK"],
            defaultOption: "OK",
            setResult: result => {
                setConfirmProps(null);
            }
        });
    };

    const refreshDocumentTypesFromResult = async (result: api.DocumentType[]) => {
        if (result) {
            // populate the sub types
            for (var t = 0; t < result.length; t++) {
                result[t].subTypes = result.filter(r => { return r.parentId === result[t].id; });
            }

            // set the list to the root notes
            setDocumentTypes(result.filter(r => { return r.parentId === null }));
        }
    };

    const refreshDocumentsFromServer = async (result: api.Document<api.DocumentMetadata>[]) => {
        if (result) {
            setAllDocuments(result);

            if (selectedDocuments.length > 0) {
                const currentDocumentIds = selectedDocuments.map(d => d.id);

                // re-select the documents using the IDs
                setSelectedDocuments(result.filter(d => currentDocumentIds.indexOf(d.id) > -1));
            }
        }
    };

    interface IRemoteUpdate {
        getData: () => Promise<any[] | null>;
        processData: (result: any[] | null) => void;
    };

    const [documentColumns] = useState([
        { title: "RP Document title", name: "document.rpTitle", selected: false },
        { title: "D2 Document title", name: "document.d2Title", selected: false },
        { title: "Document number", name: "document.documentNumber", selected: false },
        { title: "Document type", name: "document.type", selected: false },
        { title: "Document sub-type", name: "document.subType", selected: false },
        { title: "RP Published date", name: "document.d2DocumentRPPublishDate", selected: false, formatter: dateToStringFormatter },
        { title: "D2 Published date", name: "document.d2DocumentModifiedDate", selected: false, formatter: dateToStringFormatter },
        { title: "D2 Effective date", name: "document.d2DocumentEffectiveDate", selected: false, formatter: dateToStringFormatter },
        { title: "D2 Expiry date", name: "document.d2DocumentExpiryDate", selected: false, formatter: dateToStringFormatter },
        { title: "RP Document Restored", name: "document.rpDocumentRestored", selected: false, formatter: booleanToYesNoFormatter },
        { title: "Authors", name: "document.d2DocumentAuthors", selected: false },
        { title: "D2 Release Notes", name: "document.d2ReleaseNotes", selected: false },
    ] as api.ExportColumn[]);

    const [exportColumns, setExportColums] = useState([] as ISelectedExportColumn[]);

    const [sortOrder, setSortOrder] = useState([] as api.ISortConfig[]);
    useEffect(() => {
        if (exportResults && exportResults.totalResults > 0) {
            refreshExportResults(pageNumber);
        }
    }, [sortOrder]);

    const remoteUpdates = [
        { getData: api.getDocumentTypes, processData: refreshDocumentTypesFromResult },
        { getData: api.getAllD2Documents, processData: refreshDocumentsFromServer }
    ] as IRemoteUpdate[];

    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);

    const processRemoteResults = (results: PromiseSettledResult<any[] | null>[]) => {
        results.forEach((result, i) => {
            if (result.status === "fulfilled" && result.value != null) {
                remoteUpdates[i].processData(result.value);
            }
            else {
                const errorMessage = `error retrieving ${remoteUpdates[i].getData.name}`;
                console.error(errorMessage);
                msgBox(errorMessage);
            }
        });
    };

    const getVisibleColumns = () => {
        return exportColumns
            .filter(xc => {
                if (exportDocuments && documentColumns.findIndex(uc => uc.name === xc.definition.name) > -1) {
                    return true
                }

                return false;
            });
    };

    const getExportFilter = (page: number) => {
        if (
            // document filters
            !selectAllDocuments
            && selectedDocuments.length === 0
            && selectedDocumentTypes.length === 0
            && publishedDateFrom == null
            && publishedDateTo == null
        ) {
            confirm("You haven't selected any data. Please make a selection to run a report.", ["OK"], "OK");
            return null;
        }

        if (exportColumns.length === 0) {
            confirm("You haven't selected any fields. Please make a selection to run a report.", ["OK"], "OK");
            return null;
        }

        return {
            documents: exportDocuments ? {
                ids: selectedDocuments.map(u => u.id),
                typeIds: selectedDocumentTypes.map(dt => dt.id),
                publishedDateFrom: publishedDateFrom,
                publishedDateTo: publishedDateTo,
            } : null,
            pageSize: pageSize,
            pageNumber: page,
            sort: sortOrder,
            fileType: "CSV",
            exportColumns: getVisibleColumns().map(c => { return { name: c.definition.name, title: c.definition.title } as api.ExportColumn })
        } as api.UsersDocumentsFilter;
    };

    const refreshExportResults = async (page: number) => {
        if (exportState === "ready") {
            setPageNumber(page);

            const filter = getExportFilter(page);

            if (filter) {
                setExportState("busy");

                api.getD2Documents(filter)
                    .then(result => {
                        setExportState("ready");
                        setExportResults(result);
                        if (result.totalResults > MaxResultsPerReport) {
                            msgBox(<>
                                <div>Resulting data set too large.</div>
                                <div>Select less data or download full extract.</div>
                            </>);
                        }
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    useEffect(() => {
        let mounted = true;

        const mount = async () => {
            let results = await Promise.allSettled(remoteUpdates.map(r => r.getData()));
            if (mounted && results) {
                processRemoteResults(results);
            }
        };

        mount();

        return () => { mounted = false };
    }, []);

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const setSelectAllDocumentsAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all documents? Including all users will reset all document filters.", ["All Documents", "Cancel"], "All Documents").then(result => {
                if (result === "All Documents") {
                    setSelectedDocuments([]);
                    setSelectedDocumentTypes([]);
                    setPublishedDateFrom(null);
                    setPublishedDateTo(null);

                    setSelectAllDocuments(true);
                    setExportDocuments(true);
                } else {
                    setSelectAllDocuments(false);
                }
            });
        } else {
            setSelectAllDocuments(value);
        }
        setExportResults(null);
    };

    const documentNameFilter = (name: string, documents?: api.Document<api.DocumentMetadata>[]) => {
        if (!documents) {
            return [] as api.Document<api.DocumentMetadata>[];
        }

        const result = documents.filter(doc => documentNameItemFilter(doc, name));

        return result;
    };

    const documentNameItemFilter = (doc: api.Document<api.DocumentMetadata>, filterText: string) => {
        const lcName = filterText?.toLowerCase();

        return (
            doc.metadata.title.toLowerCase().indexOf(lcName) > -1
            || doc.metadata.documentNumber.toLowerCase().indexOf(lcName) > -1
            || doc.fileName.toLowerCase().indexOf(lcName) > -1
            || doc.type.toLowerCase().indexOf(lcName) > -1
            || doc.subType?.toLowerCase().indexOf(lcName) > -1
        );
    }

    // when the user changes a document filter, turn on exporting documents, and turn off "Select All Documents"
    const documentFilterAdded = () => {
        setExportDocuments(true);
        setExportResults(null);

        if (selectAllDocuments) {
            setSelectAllDocuments(false);
        }
    }

    const toggleDocument = (doc: api.Document<api.DocumentMetadata>): void => {
        if (toggleItem(doc, selectedDocuments, d => { return d.id === doc.id }, setSelectedDocuments) === true) {
            documentFilterAdded();
        }
    };

    const toggleDocumentType = (type: api.DocumentType) => {
        if (toggleItem(type, selectedDocumentTypes, dt => { return dt.id === type.id }, setSelectedDocumentTypes) === true) {
            documentFilterAdded();
        }
    };

    const toggleExportColumn = (definition: api.ExportColumn) => {
        const idx: number = exportColumns.findIndex(c => { return c.definition.name == definition.name; });

        if (idx === -1) {
            // add the column
            exportColumns.push({
                seq: exportColumns.length,
                definition: definition
            })

            // update the state
            setExportColums(exportColumns);
        } else {
            // remove the column
            const cols = exportColumns;
            cols.splice(idx, 1);

            // update the sequence numbers for any subsequent columns, so that if we add another it we dont get duplicate sequence numbers
            for (var x = idx; x < cols.length; x++) {
                cols[x].seq = x;
            }

            // update the state with the refined list
            setExportColums(cols);
        }

        forceStateChange();
    };

    const selectDocuments = (items: api.DocumentType[]) => {
        selectDocumentItems(items, selectedDocumentTypes, setSelectedDocumentTypes);
    }

    const selectDocumentItems = (items: api.DocumentType[], stateItems: api.DocumentType[], setter: (items: api.DocumentType[]) => void) => {
        const newDocs = [...new Set([...stateItems, ...items])];
        documentFilterAdded();
        setter(newDocs);
        forceStateChange();
    };

    const deselectDocumentItems = (items: api.DocumentType[], stateItems: api.DocumentType[], setter: (items: api.DocumentType[]) => void) => {
        const newDocs = [...stateItems].filter(user => !items.includes(user));
        documentFilterAdded();
        setter(newDocs);
        forceStateChange();
    };

    const deselectDocuments = (items: api.DocumentType[]) => {
        deselectDocumentItems(items, selectedDocumentTypes, setSelectedDocumentTypes);
    }

    const allExportTypeCheckedValue = (columns: api.ExportColumn[]) => {
        const selectedColumnCount = exportColumns
            .map(selCol => selCol.definition)
            .filter(exportCol => columns.includes(exportCol))
            .length;

        if (selectedColumnCount === 0) {
            return false;
        }

        if (selectedColumnCount === columns.length) {
            return true;
        }

        return 'partial';
    }

    const unselectColumns = (selectedColumns: api.ExportColumn[]) => {
        setExportColums(exportColumns.filter(selCol => !selectedColumns.includes(selCol.definition)));
    }

    const selectColumns = (columns: api.ExportColumn[]) => {
        let newExportColumns = [...exportColumns];
        columns.forEach(exportCol => {
            if (!newExportColumns.map(selCol => selCol.definition).includes(exportCol)) {
                newExportColumns.push({
                    seq: newExportColumns.length,
                    definition: exportCol
                });
            }
        });
        setExportColums(newExportColumns);
    }

    // Document by type
    const documentTypeNameContains = (item: api.DocumentType, name: string): boolean => {
        const lcFilterText = name.toLowerCase();

        if (item.name.toLowerCase().indexOf(lcFilterText) > -1) {
            return true;
        }

        if (item.subTypes) {
            for (var s = 0; s < item.subTypes.length; s++) {
                if (documentTypeNameContains(item.subTypes[s], lcFilterText)) {
                    return true;
                }
            }
        }

        return false;
    };

    const documentTypeNameFilter = (name: string, items?: api.DocumentType[]) => {
        if (!items) {
            return [] as api.DocumentType[];
        }

        const result = items.filter(item => documentTypeNameContains(item, name));

        return result;
    };

    const toggleAllExportTypeChecked = (columns: api.ExportColumn[]) => {
        const selectedColumns = exportColumns
            .map(selCol => selCol.definition)
            .filter(exportCol => columns.includes(exportCol));

        // All columns currently selected, unselect all from this group
        if (selectedColumns.length === columns.length) {
            unselectColumns(selectedColumns);
            return;
        }

        // Empty or partial selection, select all from this group not already selected
        selectColumns(columns);
    }

    return (
        <PortalPage module=""
            page="reporting"
            title={"D2 Document update reporting"}
            route={"/reporting/d2-document-update"}
            loadingState={exportState}
            icon={ReportingIcon}
            confirmationProps={confirmProps}>

            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header" onClick={() => { } }>
                        <Checkbox checkedValue={exportDocuments} />
                        <span className="icon"><FolderOutlined /></span>
                        <span className="text">D2 Document</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body top-border">
                        <LabelledCheckbox
                            checkedValue={selectAllDocuments}
                            toggle={() => { setSelectAllDocumentsAndClearSelections(!selectAllDocuments); }}>Select all documents</LabelledCheckbox>
                    </div>

                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={documentFilterTab} variant="scrollable" onChange={(e, v) => { setDocumentFilterTab(v); }}>
                                <Tab label="Find a document" />
                                <Tab label="Document type" />
                                <Tab label="Published date range" />
                            </Tabs>

                            <TabPanel value={documentFilterTab} index={0} className="tab-panel-documents">
                                <List<api.Document<api.DocumentMetadata>>
                                    listItemType={DocumentListItem}
                                    filterText={documentsByNameFilter}
                                    setFilterText={setDocumentsByNameFilter}
                                    expanded={documentsByNameExpanded}
                                    setExpanded={setDocumentsByNameExpanded}
                                    buildKey={item => item.id}
                                    dataSource={allDocuments}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    filter={documentNameFilter}
                                    itemFilter={documentNameItemFilter}
                                    toggleSelected={toggleDocument}
                                    noResultsMessage="No documents found"
                                    filterTextPlaceholder="Enter document title, document number or keyword"
                                    selected={selectedDocuments} />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={1}>
                                <List<api.DocumentType>
                                    listItemType={DocumentTypeListItem}
                                    filterText={documentsByTypeFilter}
                                    setFilterText={setDocumentsTypeFilter}
                                    expanded={documentsByTypeExpanded}
                                    setExpanded={setDocumentsByTypeExpanded}
                                    buildKey={item => item.id}
                                    dataSource={documentTypes}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    filter={documentTypeNameFilter}
                                    itemFilter={documentTypeNameContains}
                                    toggleSelected={toggleDocumentType}
                                    selectItems={selectDocuments}
                                    deselectItems={deselectDocuments}
                                    noResultsMessage="No documents types found"
                                    selected={selectedDocumentTypes} />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={2}>
                                <DateRangePicker
                                    className="date-range-picker"
                                    fromDate={publishedDateFrom ?? undefined}
                                    toDate={publishedDateTo ?? undefined}
                                    setter={(f, t) => {
                                        setPublishedDateRange(f ? f : null, t ? t : null);
                                    }}
                                />
                            </TabPanel>
                        </Paper>
                    </div>

                </div>

                <div className={`section-2 ${!exportDocuments ? "d-none" : ""}`}>
                    <div className="header">
                        <span className="icon"><DashboardCustomise /></span>
                        <span className="text">Customise report fields</span>
                    </div>
                </div>

                {exportDocuments && <>
                    <div className="section-3">
                        <div className="header">
                            <Checkbox
                                checkedValue={allExportTypeCheckedValue(documentColumns)}
                                toggle={() => toggleAllExportTypeChecked(documentColumns)} />
                            <CreateNewFolderOutlined className="ml-3" />
                            <span className="icon">Document Fields</span>
                        </div>
                        <div className="divider" />
                    </div>

                    <div className="section-4">
                        <div className="body">
                            <ExportColumnList {...standardGridSizes} columns={documentColumns} toggle={toggleExportColumn} selected={exportColumns} />
                        </div>
                    </div>
                </>}

                {exportDocuments &&
                    <div className="section-3">
                        <div className="body" style={{ textAlign: "center" }}>
                            <button className={"button-face export-state-" + exportState} onClick={() => { refreshExportResults(1); }}>Fetch data</button>
                        </div>
                    </div>
                }

                {((exportState === 'busy')) &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

                {(exportDocuments) && exportResults &&
                    <>
                        <div className="section-1 results">
                            <div className="header">
                                <span className="icon"><BarChartOutlined /></span>
                                <span className="text">Results</span>
                            </div>
                        </div>
                        {exportResults.totalResults == 0 ?
                            <div className="export-grid-no-results">No results are available for the current report</div>
                            :
                            <>
                                <div className="section-2 results">
                                    <div className="body">
                                        <ExportGrid
                                            columns={exportColumns}
                                            results={exportResults}
                                            pageNumber={pageNumber}
                                            pageSize={pageSize}
                                            state={exportState}
                                            moveToPage={refreshExportResults}
                                            setPageSize={setPageSize}
                                            sortOrder={sortOrder}
                                            removeColumn={col => { toggleExportColumn(col); }}
                                            setSortOrder={
                                                (arr) => {
                                                    setSortOrder(arr);
                                                    forceStateChange();
                                                }
                                            } />
                                    </div>
                                </div>                                
                            </>
                        }
                    </>
                }
            </div>
        </PortalPage>
    );
}

export default D2DocumentReport;